// @greenlight-spec: 07-source-control-pipeline.md
// @greenlight-spec: 16-api-conventions.md
// @greenlight-spec: 17-code-organization.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { GIT_OBJECT_SHA_REGEX, PaginationQuerySchema, PipelineRunDetailSchema, PipelineRunSchema, PipelineRunStatusSchema, paginated, } from '@greenlight/shared';
import { AppIdParamsSchema, errorResponses } from './common.js';
const c = initContract();
// Payload posted by the SCM-side CI workflow after scanners finish. See
// 07-source-control-pipeline.md §Scanner callback contract. Persisted into
// pipeline_runs then handed to the orchestrator via the @greenlight/queue job.
export const ScannerCompletePayloadSchema = z.object({
    app_id: z.string().uuid(),
    commit_sha: z.string().regex(GIT_OBJECT_SHA_REGEX),
    branch: z.string().min(1),
    is_default_branch: z.boolean(),
    scm_run_id: z.string().min(1),
    // PR that drove this run, when the commit is a PR head. Persisted on the run so the
    // getPipelineRun `pull_request_number` selector and returned field resolve. Omitted
    // for direct-to-default-branch (merge) commits.
    pull_request_number: z.number().int().positive().optional(),
    evidence_artifact: z.string().min(1),
    evidence_artifact_id: z.string().min(1),
    // Present only when the run skipped its tree scanners because Greenlight told it the
    // tree was already verified (07-source-control-pipeline.md §Reusing verified evidence
    // for an unchanged tree). A claim about what the runner did, not a fact the gate may
    // act on: the gate re-reads the tree from the SCM and rules against a verified record
    // of *that*, so a wrong or forged value costs a red run rather than a green one.
    scan_reuse: z
        .object({
        tree_sha: z.string().regex(GIT_OBJECT_SHA_REGEX),
    })
        .optional(),
    scanners: z.array(z.object({
        name: z.string().min(1),
        outcome: z.enum(['success', 'failure', 'skipped']),
    })),
    // Image build+push outcome — the CI runner builds and pushes the app image to ACR
    // on the default branch (ADR-0017). Optional: feature-branch runs and older
    // workflows omit it. The control plane's authoritative gate is the ACR
    // image-presence check (T-042), so a missing/failed build surfaces there too.
    build: z
        .object({
        outcome: z.enum(['success', 'failure', 'skipped']),
        image_ref: z.string().min(1).optional(),
        // The manifest digest of the image a private install's runner handed over as an
        // artifact (ADR-0091), which the control plane pushes and checks the registry against.
        digest: z
            .string()
            .regex(/^sha256:[0-9a-f]{64}$/)
            .optional(),
        media_type: z.string().min(1).optional(),
    })
        .optional(),
    // Runner-side wall-clock stamps, so a run accounts for the whole merge→live window
    // instead of only its deploy stage (docs/11 §Pipeline stage timings). Each field is
    // null when its step did not run — `push_completed_at` on every PR-branch run — and
    // the whole block is absent from workflows older than the render that emits it.
    timings: z
        .object({
        // The merge that started this run, from the push event. `triggered_at` is
        // otherwise stamped when this callback arrives — after checkout, build, and
        // push — which puts every runner stamp in its past and makes merge→live
        // unmeasurable. Absent on workflow renders older than the one that sends it.
        merged_at: z.string().datetime({ offset: true }).nullish(),
        runner_started_at: z.string().datetime({ offset: true }).nullish(),
        build_started_at: z.string().datetime({ offset: true }).nullish(),
        build_completed_at: z.string().datetime({ offset: true }).nullish(),
        push_completed_at: z.string().datetime({ offset: true }).nullish(),
    })
        .optional(),
});
// Receivers that enqueue async work return 202 with the canonical row id.
// See 16-api-conventions.md §Async Work and 202 Responses.
export const PipelineAcceptedSchema = z.object({
    run_id: z.string().uuid(),
    status: PipelineRunStatusSchema,
});
export const RegistryPushTokenRequestSchema = z.object({
    app_id: z.string().uuid(),
});
// Asked by the CI workflow after checkout, before it scans: does Greenlight already hold
// verified evidence for these exact bytes? See 07-source-control-pipeline.md §Reusing
// verified evidence for an unchanged tree.
export const ScanReuseRequestSchema = z.object({
    app_id: z.string().uuid(),
    // GitHub repositories use SHA-1 today; accept SHA-256 as Git's other full object format.
    // Abbreviated hashes cannot match the SCM's full tree id and are rejected rather than
    // silently turning into a misleading `reuse: false`.
    tree_sha: z.string().regex(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/),
    is_default_branch: z.boolean(),
});
export const ScanReuseResponseSchema = z.object({
    // True when the runner may skip its tree scanners. An optimization hint, never a
    // verdict — the gate independently re-derives the tree and re-verifies the evidence
    // it reuses, so a runner that skips on a stale `true` gets a red run.
    reuse: z.boolean(),
    // Why reuse was refused, for the run log. Absent when `reuse` is true.
    reason: z.string().optional(),
});
export const RegistryPushTokenResponseSchema = z.object({
    registry_server: z.string().min(1),
    // Fully-qualified `HOST/…/gl-app-<slug>` repository path (no tag) to tag the pushed
    // image at. `registry_server` is only the login host; on Artifact Registry the
    // repository path additionally carries the project/repo segments, so the CI workflow
    // pushes to this rather than assembling a path from the host itself.
    image_repository: z.string().min(1),
    username: z.string().min(1),
    password: z.string().min(1),
    expires_at: z.string().datetime(),
});
export const pipelineContract = c.router({
    listRuns: {
        method: 'GET',
        path: '/api/apps/:id/runs',
        pathParams: AppIdParamsSchema,
        query: PaginationQuerySchema,
        responses: {
            200: paginated(PipelineRunSchema),
            ...errorResponses,
        },
        summary: 'List pipeline runs for an app (cursor-paginated by triggered_at desc).',
    },
    getRun: {
        method: 'GET',
        path: '/api/apps/:id/runs/:runId',
        pathParams: AppIdParamsSchema.extend({ runId: z.string().uuid() }),
        // `full` attaches each check's raw log tail (build/scan proxied live from the SCM,
        // deploy's crash snapshot) and is audited; `summary` is the cheap pollable verdict.
        // The MCP-only selector/`wait` params are not exposed on REST (06-agent-interface.md).
        query: z.object({
            detail: z.enum(['summary', 'full']).default('summary'),
        }),
        responses: {
            200: PipelineRunDetailSchema,
            ...errorResponses,
        },
        summary: 'Fetch a single pipeline run with its per-check rows and OPA decision.',
    },
    scannerComplete: {
        method: 'POST',
        path: '/api/internal/pipeline/scanner-complete',
        body: ScannerCompletePayloadSchema,
        responses: {
            202: PipelineAcceptedSchema,
            ...errorResponses,
        },
        summary: 'Internal: CI scanner callback. Enqueues the orchestrator job.',
    },
    resolveScanReuse: {
        method: 'POST',
        path: '/api/internal/pipeline/scan-reuse',
        body: ScanReuseRequestSchema,
        responses: {
            200: ScanReuseResponseSchema,
            ...errorResponses,
        },
        summary: 'Internal: whether the CI run may skip tree scanners for an already-verified tree.',
    },
    requestRegistryPushToken: {
        method: 'POST',
        path: '/api/internal/registry/push-token',
        body: RegistryPushTokenRequestSchema,
        responses: {
            200: RegistryPushTokenResponseSchema,
            ...errorResponses,
        },
        summary: 'Internal: CI workflow registry push credentials for an app image.',
    },
});
//# sourceMappingURL=pipeline.js.map